import { useState, useEffect } from "react";
import { CustomSelect } from "@/components/CustomSelect";
import { DateInput } from "@/components/DateInput";
import { fmtDate } from "@/lib/dates";
import { Plus, PiggyBank, Trash2, Check, History } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/Button";
import { Modal, Field, TextInput } from "@/components/Modal";
import { EmptyState, Loader } from "@/components/common";
import { ContributionHistory } from "@/components/space/ContributionHistory";

const GOAL_CATS = ["Date Night", "Vacation", "Christmas", "Birthday", "Furniture", "Car Repairs", "Wedding", "Baby", "Emergency Fund", "Custom"];

export function SavingsSection({ cid }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [contribFor, setContribFor] = useState(null);
  const [amount, setAmount] = useState("");
  const [historyId, setHistoryId] = useState(null);
  const historyGoal = items.find((g) => g.id === historyId);

  const removeContribution = async (c) => {
    if (!confirm(`Remove your $${Number(c.amount).toFixed(2)} contribution?`)) return;
    try { await api.delete(`/connections/${cid}/savings/${historyId}/contributions/${c.id}`); await load(true); toast.success("Contribution removed"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try { const { data } = await api.get(`/connections/${cid}/savings`); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  const startAdd = () => { setForm({ name: "", target_amount: "", target_date: "", category: "Date Night" }); setOpen(true); };

  const save = async () => {
    if (!form.name?.trim() || !form.target_amount) return toast.error("Enter a name and target amount.");
    setBusy(true);
    try {
      await api.post(`/connections/${cid}/savings`, { name: form.name, target_amount: Number(form.target_amount), target_date: form.target_date || null, category: form.category });
      setOpen(false); await load(); toast.success("Goal created");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const contribute = async () => {
    if (!amount || Number(amount) <= 0) return toast.error("Enter an amount.");
    setBusy(true);
    try {
      const { data } = await api.post(`/connections/${cid}/savings/${contribFor.id}/contribute`, { amount: Number(amount) });
      setContribFor(null); setAmount(""); await load();
      toast.success(data.status === "completed" ? "🎉 Goal completed!" : "Contribution added");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const del = async (g) => { if (confirm("Delete this goal?")) { await api.delete(`/connections/${cid}/savings/${g.id}`); await load(); } };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="flex justify-end mb-4"><Button onClick={startAdd} data-testid="add-goal-btn"><Plus size={16} /> New Goal</Button></div>
      {items.length === 0 ? (
        <EmptyState icon={PiggyBank} title="No savings goals yet" subtitle="Track a shared goal together. The app just tracks progress — it never touches money." action={<Button onClick={startAdd}><Plus size={16} /> New Goal</Button>} testId="savings-empty" />
      ) : (
        <div className="space-y-3">
          {items.map((g) => {
            const pct = Math.min(100, Math.round((g.current_amount / g.target_amount) * 100));
            const done = g.status === "completed";
            return (
              <div key={g.id} className="iks-card p-5 group" data-testid="savings-goal">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-sky-400">{g.category}</span>
                    <h3 className="font-display font-semibold text-slate-50">{g.name}</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    {done && <span className="text-xs text-emerald-400 flex items-center gap-1"><Check size={13} /> Done</span>}
                    <button onClick={() => del(g)} className="p-1 text-slate-500 hover:text-red-400 opacity-60 group-hover:opacity-100 transition-opacity"><Trash2 size={15} /></button>
                  </div>
                </div>
                <button onClick={() => setHistoryId(g.id)} className="block w-full text-left" data-testid="goal-open-history">
                <div className="flex items-end justify-between mt-3 mb-2">
                  <span className="text-2xl font-display font-bold text-slate-50">${g.current_amount.toFixed(0)}</span>
                  <span className="text-slate-500 text-sm">of ${g.target_amount.toFixed(0)}{g.target_date ? ` · by ${fmtDate(g.target_date)}` : ""}</span>
                </div>
                <div className="h-2.5 rounded-full bg-slate-800 overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${done ? "bg-emerald-500" : "bg-gradient-to-r from-blue-600 to-sky-400"}`} style={{ width: `${pct}%` }} />
                </div>
                {g.contributions?.length > 0 && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-slate-400">
                    {Object.entries(g.contributions.reduce((a, c) => { a[c.user_name] = (a[c.user_name] || 0) + c.amount; return a; }, {})).map(([n, amt]) => (
                      <span key={n}>{n}: <span className="text-sky-300">+${amt.toFixed(0)}</span></span>
                    ))}
                  </div>
                )}
                </button>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button variant="secondary" className="!py-2" onClick={() => { setContribFor(g); setAmount(""); }} data-testid="contribute-btn"><Plus size={15} /> Add contribution</Button>
                  <Button variant="ghost" className="!py-2" onClick={() => setHistoryId(g.id)} data-testid="goal-history-btn"><History size={15} /> History</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New savings goal" testId="goal-modal"
        footer={<><Button variant="secondary" full onClick={() => setOpen(false)}>Cancel</Button><Button full loading={busy} onClick={save} data-testid="save-goal-btn">Create</Button></>}>
        <Field label="Goal name"><TextInput value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Date Night Fund" data-testid="goal-name" /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 [&>*]:min-w-0">
          <Field label="Target amount"><TextInput type="number" value={form.target_amount} onChange={(e) => setForm((f) => ({ ...f, target_amount: e.target.value }))} placeholder="80" data-testid="goal-target" /></Field>
          <Field label="Target date (optional)"><DateInput value={form.target_date} onChange={(e) => setForm((f) => ({ ...f, target_date: e.target.value }))} data-testid="goal-target-date" /></Field>
        </div>
        <Field label="Category">
          <CustomSelect options={GOAL_CATS} value={form.category} onChange={(v) => setForm((f) => ({ ...f, category: v }))} placeholder="e.g. New TV" testId="goal-category" />
        </Field>
      </Modal>

      <ContributionHistory open={!!historyGoal} onClose={() => setHistoryId(null)} title={`${historyGoal?.name || ""} history`}
        contributions={historyGoal?.contributions} onRemove={removeContribution} />

      <Modal open={!!contribFor} onClose={() => setContribFor(null)} title={`Contribute to ${contribFor?.name || ""}`} testId="contribute-modal"
        footer={<><Button variant="secondary" full onClick={() => setContribFor(null)}>Cancel</Button><Button full loading={busy} onClick={contribute} data-testid="save-contribution-btn">Add</Button></>}>
        <Field label="Amount"><TextInput type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="20" autoFocus data-testid="contribution-amount" /></Field>
      </Modal>
    </div>
  );
}
